import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Grid, CellComponentProps } from 'react-window';

import { PieceAssetSize } from '@apiContract';
import { ClickableBox } from '@uiKit/ClickableBox';
import { getPieceAsset } from 'src/utils/piece';

import {
  sxImage,
  sxImageListContainer,
  sxImageTitle,
  sxImagesCell,
} from '@components/Capture/components/AutomatedCapture/styles';
import { CapturePiece } from '@components/Capture/types';
import { formatLongTimeString } from '@utils/stringFormatters';

import ImageLightbox from './ImageLightbox';
import { Slide } from 'yet-another-react-lightbox';

type Props = {
  items: CapturePiece[];
};

const FilesList: FC<Props> = ({ items }) => {
  const [lightbox, setLightbox] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));

  const cellGap = 24;
  const itemHeight = 250;
  const columnCount = isMobile ? 2 : isMedium ? 4 : 6;
  const rowCount = Math.ceil(items.length / columnCount);

  const slides = useMemo(() => {
    return items.map((item) => ({ src: getPieceAsset(item?.piece?.pieceAssets || [], PieceAssetSize.Large)?.url })) as Slide[];
  }, [items]);

  const handleOpenLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightbox(true);
  };
  const Cell = ({ columnIndex, rowIndex, style }: CellComponentProps) => {
    const itemIndex = rowIndex * columnCount + columnIndex;

    if (itemIndex >= items.length) return <></>;

    const item = items[itemIndex];
    const imgUrl = getPieceAsset(item.piece?.pieceAssets || [], PieceAssetSize.Medium)?.url || '';
    const imgTime = item.piece?.created ? formatLongTimeString(item.piece.created) : 'N/A';

    return (
      <Box
        sx={{
          ...style,
          left: columnIndex === 0 ? style.left : Number(style.left) + columnIndex * cellGap,
          right: columnIndex === columnCount ? style.right : Number(style.right) + columnIndex * cellGap,
          bottom: rowIndex === 0 ? style.bottom : Number(style.bottom) + rowIndex * cellGap,
        }}
      >
        <ClickableBox onClick={() => handleOpenLightbox(itemIndex)} sx={sxImagesCell}>
          <Box component="img" src={imgUrl} alt={imgTime} sx={sxImage} />
          <Box
            display="flex"
            alignItems="center"
            sx={{
              height: '50px',
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={sxImageTitle}>
              {imgTime}
            </Typography>
          </Box>
        </ClickableBox>
      </Box>
    );
  };

  return (
    <>
      <Box sx={sxImageListContainer(theme)}>
        <AutoSizer>
          {({ height, width }) => {
            const hasVerticalScrollbar = rowCount * itemHeight > height;
            const effectiveWidth = (hasVerticalScrollbar ? width - 8 : width) - cellGap * (columnCount - 1);

            return (
              <Grid
                columnCount={columnCount}
                columnWidth={effectiveWidth / columnCount}
                cellComponent={Cell}
                rowCount={rowCount}
                rowHeight={itemHeight}
                className="customScrollBar"
                cellProps={{}}
              />
            );
          }}
        </AutoSizer>
      </Box>
      <ImageLightbox slides={slides} isOpen={lightbox} setLightbox={setLightbox} initialIndex={currentIndex} />
    </>
  );
};

export default FilesList;
